"use client";

import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { CircleCheck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useEffect, useRef, useState } from "react";
export default function AudioEquipmentOverview() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  const imageRef = useRef<HTMLDivElement>(null);
  const [parallaxOffset, setParallaxOffset] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (imageRef.current) {
        const rect = imageRef.current.getBoundingClientRect();
        const scrollProgress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
        setParallaxOffset(scrollProgress * 30);
      }
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const equipment = [{
    id: 1,
    text: "Professional-grade mixing consoles and controllers"
  }, {
    id: 2,
    text: "High-fidelity speaker systems for crystal-clear sound"
  }, {
    id: 3,
    text: "Dynamic LED lighting rigs with customizable color palettes"
  }, {
    id: 4,
    text: "Wireless microphone setups for toasts and announcements"
  }, {
    id: 5,
    text: "Intelligent lighting design tailored to your venue"
  }];
  return <section id="audio-equipment-overview" ref={ref} className="relative py-16 md:py-24 bg-background overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {}
        <div className="flex justify-center mb-12">
          
        </div>

        <div className={`grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-center transition-all duration-[var(--duration-medium)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          {}
          <div className="lg:col-span-2 relative">
            <div ref={imageRef} className="relative rounded-lg overflow-hidden shadow-strong" style={{
            transform: `translateY(${parallaxOffset}px)`,
            transition: "transform 0.1s ease-out"
          }}>
              <img src="https://denverweddingco.com/wp-content/uploads/2026/01/DSC4142.jpg" alt="Professional DJ equipment and lighting setup" className="w-full h-auto object-cover" />
            </div>
          </div>

          {}
          <div className="lg:col-span-3 flex flex-col gap-6">
            <div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
                Premium Audio & Lighting Equipment
              </h2>
              <p className="text-lg text-muted-foreground">
                Your Denver wedding deserves the highest quality sound and
                lighting. With years of experience and professional-grade
                equipment, Denver Wedding CO will ensure every moment is
                perfectly amplified and beautifully illuminated.
              </p>
            </div>

            <ul className="flex flex-col gap-4">
              {equipment.map((item, i) => <li key={item.id} data-index={i} className="flex items-start gap-3 group transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] hover:-translate-y-0.5 hover:shadow-medium">
                  <CircleCheck className="size-6 text-foreground mt-0.5 shrink-0 transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] group-hover:-translate-y-0.0 group-hover:drop-shadow-[0_4px_8px_rgba(0,188,212,0.3)]" />
                  <span className="text-base text-foreground">{item.text}</span>
                </li>)}
            </ul>

            <div className="mt-4">
              <Button asChild variant="default" size="lg" className="group relative overflow-hidden">
                <Link to="/LetsTalkPage">
                  <span className="relative z-10">Discuss Your Setup</span>
                  <span className="absolute inset-0 bg-accent-hover opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-sm" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}